import React, { useState, useEffect, useCallback, useMemo } from 'react';
import GoogleMapReact, { ChangeEventValue } from 'google-map-react';
import { useLocation } from 'react-router-dom';
import { Tooltip, Grid, Tabs, Tab } from '@material-ui/core';

import { loadSimovsatVehicles } from '~/services/api';
import {
  Divider,
  ContainerModal,
  TitleModal,
  Minitext,
  RefreshButton,
  MapControls,
  SimplePin,
} from '../MapOpportunities/styles';
import Colors from '~/styles/colors';
import SimovsatVehicleInfo from '../MapOpportunities/components/SimovsatVehicleInfo';
import { handleApiErrorResponse } from '~/services/handleErrors';
import { SimovsatVehicle, SimovsatVehiclesResponse } from '../../types';
import { appendQueryParamsTo } from '../../services/urlutils';
import Snackbar from '../../components/Snackbar/index';
import { SeverityType } from '../../components/Snackbar/index';
import Modal from '~/components/ModalNewVehicle';
import { Title } from '~/components/Title';
import InputComponent from '~/components/InputComponent';
import Button from '~/components/Button';
import VehicleSearch from './components/VehicleSearch';
import Charts from './components/Charts';

const defaultMapCenter = {
  lat: -12.9453842,
  lng: -38.4942182,
};

const defaultZoom = 13;

enum SimovsatTab {
  MAP = 0,
  SEARCH = 1,
  GRAPHS = 2,
}

export default function Simovsat() {
  const [activeTab, setActiveTab] = useState<SimovsatTab>(SimovsatTab.MAP);
  const [positionCenter, setPositionCenter] = useState<{ lat: number; lng: number }>(defaultMapCenter);
  const [zoomPosition, setZoomPosition] = useState(defaultZoom);
  const [mapStatus, setMapStatus] = useState<ChangeEventValue>();

  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [openModalSimovsatVehicle, setOpenModalSimovsatVehicle] = useState(false);

  const [selectedSimovsatVehicle, setSelectedSimovsatVehicle] = useState<SimovsatVehicle | undefined>(undefined);

  // Estados para Simovsat
  const [simovsatVehicles, setSimovsatVehicles] = useState<SimovsatVehicle[]>([]);
  const [kmMin, setKmMin] = useState<string>('');
  const [kmMax, setKmMax] = useState<string>('');
  const [placa, setPlaca] = useState<string>('');
  const [loadingSimovsat, setLoadingSimovsat] = useState(false);

  const location = useLocation();

  const getFromQueryParams = useCallback(
    (key: string) => {
      return new URLSearchParams(location.search.slice(0)).get(key) ?? '';
    },
    [location],
  );

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(() => {
      //
    });
  }, []);

  useEffect(() => {
    const lat = Number.parseFloat(getFromQueryParams('lat'));
    const lng = Number.parseFloat(getFromQueryParams('lng'));
    const zoom = Number.parseInt(getFromQueryParams('zoom'));
    if (lat && lng) {
      setPositionCenter({ lat, lng });
    }
    if (zoom) {
      setZoomPosition(zoom);
    }
  }, [getFromQueryParams]);

  const loadSimovsatData = useCallback(async () => {
    try {
      setLoadingSimovsat(true);
      // Limpa o veículo selecionado quando recarrega os dados
      setSelectedSimovsatVehicle(undefined);
      const response = await loadSimovsatVehicles(kmMin || undefined, kmMax || undefined, placa || undefined);

      if (response.status === 200 && response.data) {
        const vehiclesData: SimovsatVehiclesResponse = response.data;
        // Filtra apenas veículos com coordenadas válidas
        const validVehicles = (vehiclesData.data || []).filter(vehicle => {
          const hasValidLat = vehicle.latitude != null && !Number.isNaN(vehicle.latitude) && vehicle.latitude !== 0;
          const hasValidLng = vehicle.longitude != null && !Number.isNaN(vehicle.longitude) && vehicle.longitude !== 0;
          return hasValidLat && hasValidLng;
        });
        setSimovsatVehicles(validVehicles);

        // Se não há veículos válidos, reseta o centro do mapa para a posição padrão
        if (validVehicles.length === 0) {
          setPositionCenter(defaultMapCenter);
          setZoomPosition(defaultZoom);
        }
      } else {
        setSimovsatVehicles([]);
        // Reseta o centro do mapa quando não há dados
        setPositionCenter(defaultMapCenter);
        setZoomPosition(defaultZoom);
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) +
        ': não foi possível carregar veículos Simovsat. Tente novamente.',
      );
      setOpenSnackbar(true);
      setSimovsatVehicles([]);
      setSelectedSimovsatVehicle(undefined);
      // Reseta o centro do mapa em caso de erro
      setPositionCenter(defaultMapCenter);
      setZoomPosition(defaultZoom);
    } finally {
      setLoadingSimovsat(false);
    }
  }, [kmMin, kmMax, placa]);

  useEffect(() => {
    loadSimovsatData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Centraliza o mapa no veículo quando uma placa é selecionada
  useEffect(() => {
    if (placa && placa.trim().length > 0 && simovsatVehicles.length > 0) {
      // Debounce: aguarda 500ms após parar de digitar antes de centralizar
      const timeoutId = setTimeout(() => {
        // Busca o veículo pela placa (case insensitive)
        const vehicle = simovsatVehicles.find(
          v => v.plate_number && v.plate_number.toUpperCase().trim() === placa.toUpperCase().trim()
        );

        // Validação rigorosa: apenas centraliza se o veículo tem coordenadas válidas
        if (vehicle) {
          const hasValidLat = vehicle.latitude != null && !Number.isNaN(vehicle.latitude) && vehicle.latitude !== 0;
          const hasValidLng = vehicle.longitude != null && !Number.isNaN(vehicle.longitude) && vehicle.longitude !== 0;

          if (hasValidLat && hasValidLng) {
            // Centraliza o mapa exatamente no veículo encontrado
            setPositionCenter({
              lat: vehicle.latitude,
              lng: vehicle.longitude,
            });
            // Aumenta o zoom para destacar o veículo (zoom 16 para visualização detalhada)
            setZoomPosition(16);
            // Seleciona o veículo para destacá-lo (mas não abre o modal automaticamente)
            setSelectedSimovsatVehicle(vehicle);
          }
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placa, simovsatVehicles]);

  const simovsatPinData = useMemo(() => {
    // Se não há veículos, retorna array vazio
    if (!simovsatVehicles || simovsatVehicles.length === 0) {
      return [];
    }

    const validPins = simovsatVehicles
      .filter(vehicle => {
        // Validação rigorosa: apenas veículos com coordenadas válidas e não nulas
        if (!vehicle || !vehicle.id) {
          return false;
        }

        const hasValidLat =
          vehicle.latitude != null &&
          !Number.isNaN(vehicle.latitude) &&
          vehicle.latitude !== 0 &&
          typeof vehicle.latitude === 'number' &&
          vehicle.latitude >= -90 &&
          vehicle.latitude <= 90;

        const hasValidLng =
          vehicle.longitude != null &&
          !Number.isNaN(vehicle.longitude) &&
          vehicle.longitude !== 0 &&
          typeof vehicle.longitude === 'number' &&
          vehicle.longitude >= -180 &&
          vehicle.longitude <= 180;

        return hasValidLat && hasValidLng;
      })
      .map(vehicle => ({
        lat: vehicle.latitude,
        lng: vehicle.longitude,
        color: vehicle.active ? Colors.green : Colors.grey,
        key: `SimovsatPin#${vehicle.id}.${vehicle.latitude}.${vehicle.longitude}`,
        data: vehicle,
      }));

    return validPins;
  }, [simovsatVehicles]);

  // Limpa o veículo selecionado se ele não estiver mais na lista de veículos válidos
  useEffect(() => {
    if (selectedSimovsatVehicle && simovsatPinData.length > 0) {
      const isStillValid = simovsatPinData.some(pin => pin.data.id === selectedSimovsatVehicle.id);
      if (!isStillValid) {
        setSelectedSimovsatVehicle(undefined);
      }
    } else if (selectedSimovsatVehicle && simovsatPinData.length === 0) {
      setSelectedSimovsatVehicle(undefined);
    }
  }, [simovsatPinData, selectedSimovsatVehicle]);

  const getRefreshRate = useCallback(() => {
    const refreshIntervalToValidValue = (value: number) => Math.max(1000, value);
    const paramsValue = Number.parseInt(getFromQueryParams('refresh_interval_ms'));
    return !Number.isNaN(paramsValue) ? refreshIntervalToValidValue(paramsValue) : 50000;
  }, [getFromQueryParams]);

  return (
    <div>
      <Title>Simovsat</Title>
      <Divider height={20} />

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => {
          setActiveTab(newValue);
        }}
        indicatorColor='primary'
        textColor='primary'
        style={{
          marginBottom: 20,
        }}
      >
        <Tab label='Mapa' />
        <Tab label='Pesquisa de Veículos' />
        <Tab label='Gráficos' />
      </Tabs>

      {activeTab === SimovsatTab.MAP && (
        <>
          <Grid container spacing={2} style={{ marginBottom: 20 }}>
            <Grid item xs={12} sm={3}>
              <InputComponent
                label='KM Mínimo'
                name='kmMin'
                type='number'
                value={kmMin}
                onChange={e => setKmMin(e.target.value)}
                error={null}
                placeholder='Ex: 100'
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <InputComponent
                label='KM Máximo'
                name='kmMax'
                type='number'
                value={kmMax}
                onChange={e => setKmMax(e.target.value)}
                error={null}
                placeholder='Ex: 1000'
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <InputComponent
                label='Placa'
                name='placa'
                value={placa}
                onChange={e => setPlaca(e.target.value.toUpperCase())}
                error={null}
                placeholder='Ex: ABC1234'
              />
            </Grid>
            <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                label='Buscar'
                loading={loadingSimovsat}
                onClick={loadSimovsatData}
                filled
                style={{ width: '100%', marginTop: 0, marginBottom: 10 }}
              />
            </Grid>
          </Grid>

          <div style={{ height: '75vh', width: '100%' }} id='map'>
            <MapControls>
              <Tooltip arrow placement='top' title='Atualizar veículos'>
                <div>
                  <RefreshButton
                    spin={loadingSimovsat ? 1 : undefined}
                    status={loadingSimovsat ? 'IDLE' : 'SUCCESS'}
                    onClick={loadSimovsatData}
                  />
                </div>
              </Tooltip>
            </MapControls>

            <GoogleMapReact
              bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY ?? '' }}
              defaultCenter={defaultMapCenter}
              defaultZoom={defaultZoom}
              center={positionCenter}
              zoom={zoomPosition}
              onChange={setMapStatus}
              yesIWantToUseGoogleMapApiInternals
            >
              {simovsatVehicles?.length > 0 &&
                simovsatPinData?.length > 0 &&
                simovsatPinData.map(el => (
                  <SimplePin
                    key={el.key}
                    color={el.color}
                    lat={el.lat}
                    lng={el.lng}
                    onClick={() => {
                      setSelectedSimovsatVehicle(el.data);
                      setOpenModalSimovsatVehicle(true);
                    }}
                  />
                ))}
            </GoogleMapReact>

            <pre>
              {mapStatus && (
                <Minitext>
                  <span>Latitude: {mapStatus.center.lat}&nbsp;</span>
                  <span>Longitude: {mapStatus.center.lng}&nbsp;</span>
                  <span>Zoom: {mapStatus.zoom}x&nbsp;</span>
                  <span>Atualização: {(getRefreshRate() / 1000).toFixed(1)}s&nbsp;</span>
                  <a
                    rel='noopener noreferrer'
                    href={appendQueryParamsTo('/simovsat', {
                      refresh_interval_ms: getRefreshRate(),
                      lat: mapStatus.center.lat,
                      lng: mapStatus.center.lng,
                      zoom: mapStatus.zoom,
                    })}
                    target='_blank'
                  >
                    Criar mapa com as configurações atuais.
                  </a>
                </Minitext>
              )}
            </pre>

            <Divider height={15} />

            <Modal
              onConfirm={() => {
                setOpenModalSimovsatVehicle(false);
              }}
              onCancel={() => {
                setOpenModalSimovsatVehicle(false);
              }}
              open={openModalSimovsatVehicle}
            >
              <ContainerModal>
                <TitleModal>Detalhes do Veículo Simovsat</TitleModal>
                <Divider height={15} />
                {selectedSimovsatVehicle && <SimovsatVehicleInfo vehicle={selectedSimovsatVehicle} />}
              </ContainerModal>
            </Modal>
          </div>
        </>
      )}

      {activeTab === SimovsatTab.SEARCH && <VehicleSearch />}

      {activeTab === SimovsatTab.GRAPHS && <Charts />}

      <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />
    </div>
  );
}
